import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { Reveal } from "./Reveal";
import { LogoPlate } from "./Logo";
import { useLang } from "../i18n";
import { PRODUCT_LINKS } from "../productLinks";
import { SOLUTIONS } from "../solutionsData";

const LockIcon = () => <Lock className="h-3 w-3" />;

const COMPANY = [
    { to: "/about", label: { en: "About", hi: "परिचय" }, id: "footer-link-about" },
    { to: "/installers", label: { en: "For installers", hi: "इंस्टॉलर के लिए" }, id: "footer-link-installers" },
    { to: "/gallery", label: { en: "Project gallery", hi: "प्रोजेक्ट गैलरी" }, id: "footer-link-gallery" },
    { to: "/warranty", label: { en: "Warranty tracker", hi: "वारंटी ट्रैकर" }, id: "footer-link-warranty" },
    { to: "/faq", label: { en: "FAQ", hi: "सवाल-जवाब" }, id: "footer-link-faq" },
    { to: "/contact", label: { en: "Contact", hi: "संपर्क" }, id: "footer-link-contact" },
    { to: "/installer", label: { en: "Installer portal", hi: "इंस्टॉलर पोर्टल" }, id: "footer-link-installer-portal" },
];

const col = "font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400 transition-colors duration-300 hover:text-brand";

export default function Footer() {
    const year = new Date().getFullYear();
    const { lang } = useLang();
    const t = (en, hi) => (lang === "hi" ? hi : en);
    const T = (v) => (v && typeof v === "object" ? v[lang] || v.en : v);
    return (
        <footer data-testid="footer-main-section" className="relative overflow-hidden border-t border-white/10 bg-footerNavy text-paper">
            <div className="hazard-line h-1.5 w-full" />
            <div className="mx-auto max-w-[1500px] px-5 pb-8 pt-14 md:px-10 md:pt-20">
                <div className="grid gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-3">
                        <Link to="/" data-testid="footer-brand-logo" aria-label="Desamatic home">
                            <LogoPlate className="h-10" />
                        </Link>
                        <p className="mt-6 max-w-xs text-sm leading-relaxed text-zinc-400">
                            {t(
                                "Manufacturer of gate, door and pedestrian automation, parking barriers and access control — Sanganer, Jaipur.",
                                "गेट, दरवाज़ा और पैदल ऑटोमेशन, पार्किंग बैरियर और एक्सेस कंट्रोल के निर्माता — सांगानेर, जयपुर।"
                            )}
                        </p>
                        <a href="tel:+919782207225" className="mt-5 block font-mono text-[11px] tracking-[0.2em] text-brand">+91 97822 07225</a>
                        <a href="mailto:desamaticautomation@gmail.com" className="mt-1 block font-mono text-[11px] tracking-[0.1em] text-zinc-400 hover:text-brand">desamaticautomation@gmail.com</a>
                    </div>
                    <nav className="lg:col-span-3" aria-label="Products">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand">{t("Products", "उत्पाद")}</p>
                        <div className="mt-4 grid gap-2.5">
                            {PRODUCT_LINKS.map((p) => (
                                <Link key={p.slug} to={`/products/${p.slug}`} data-testid={`footer-product-${p.slug}`} className={col}>
                                    {p.idx} · {T(p.title)}
                                </Link>
                            ))}
                        </div>
                    </nav>
                    <nav className="lg:col-span-3" aria-label="Solutions">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand">{t("Solutions", "समाधान")}</p>
                        <div className="mt-4 grid gap-2.5">
                            {SOLUTIONS.map((s) => (
                                <Link key={s.slug} to={`/solutions/${s.slug}`} data-testid={`footer-solution-${s.slug}`} className={col}>
                                    {T(s.title)}
                                </Link>
                            ))}
                        </div>
                    </nav>
                    <nav className="lg:col-span-3" aria-label="Company">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand">{t("Company", "कंपनी")}</p>
                        <div className="mt-4 grid gap-2.5">
                            {COMPANY.map((l) => (
                                <Link key={l.to} to={l.to} data-testid={l.id} className={col}>
                                    {l.label[lang]}
                                </Link>
                            ))}
                        </div>
                    </nav>
                </div>

                <Reveal y={40}>
                    <h2
                        data-testid="footer-oversized-wordmark"
                        className="mt-14 select-none font-display text-[17.5vw] uppercase leading-[0.85] tracking-tight"
                    >
                        <span className="text-outline">Desa</span>
                        <span className="text-outline-brand">matic</span>
                    </h2>
                </Reveal>

                <div className="mt-6 flex flex-col gap-3 border-t border-white/10 pt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 md:flex-row md:items-center md:justify-between">
                    <span>© {year} Desamatic Automation</span>
                    <span>{t("Manufacturer of entrance automation & access control systems", "प्रवेश ऑटोमेशन और एक्सेस कंट्रोल प्रणालियों के निर्माता")}</span>
                    <div className="flex items-center gap-4">
                        <span>{t("Jaipur, Rajasthan, India", "जयपुर, राजस्थान, भारत")}</span>
                        <Link
                            to="/admin"
                            data-testid="footer-staff-login"
                            className="flex items-center gap-1.5 text-zinc-400 transition-colors duration-300 hover:text-brand"
                        >
                            <LockIcon /> {t("Staff & dealer login", "स्टाफ व डीलर लॉगिन")}
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
